import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NotificationsService, RequestLoaderService } from '@portal/core';
import { ContentHeaders } from './download.service.interface';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  requestLoaderService: RequestLoaderService;
  notificationsService: NotificationsService;

  constructor() {
    this.requestLoaderService = inject(RequestLoaderService);
    this.notificationsService = inject(NotificationsService);
  }

  downloadFile(resp: HttpResponse<Blob>, name: string) {
    const type = resp.headers.get(ContentHeaders.ContentType);
    const contentDispositionHeader = resp.headers.get(ContentHeaders.ContentDisposition);
    if (!type || !contentDispositionHeader) {
      return;
    }

    const fns: string[] = contentDispositionHeader.split('filename=');
    let fn: string;
    if (fns.length && fns[1]) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      fn = fns[1]?.replaceAll('"', '');
    } else {
      fn = name;
    }
    const blob = new File([resp.body], fn, { type });

    this.generateLinkAndDownload(blob, fn);
  }

  generateLinkAndDownload(blob: Blob, fileName: string) {
    const anchor = this.createTagElement(fileName);

    const reader = new FileReader();
    reader.readAsDataURL(blob);

    reader.onload = () => {
      anchor.href = reader.result as string;
      anchor.dataset.downloadurl = ['text/plain', anchor.download, anchor.href].join(':');
      // eslint-disable-next-line no-restricted-globals
      document.body.appendChild(anchor); // important in Firefox
      anchor.click();

      this.requestLoaderService.show();
    };

    reader.onloadend = () => {
      anchor.remove();
      this.requestLoaderService.hide();
    };
  }

  downloadImageWithXhr(url: string, name: string) {
    this.requestLoaderService.show();
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';

    xhr.onload = () => {
      // eslint-disable-next-line no-restricted-globals
      const urlCreator = window.URL || window.webkitURL;
      const imageUrl = urlCreator.createObjectURL(xhr.response);
      const tag = this.createTagElement(name);
      tag.href = imageUrl;
      // eslint-disable-next-line no-restricted-globals
      document.body.appendChild(tag);
      tag.click();
      // eslint-disable-next-line no-restricted-globals
      document.body.removeChild(tag);
      // eslint-disable-next-line no-restricted-globals
      window.URL.revokeObjectURL(imageUrl);
      this.notificationsService.showSuccessMessage();
    };

    xhr.onerror = () => {
      this.notificationsService.showErrorMessage();
      this.requestLoaderService.hide();
    };

    xhr.onloadend = () => {
      this.requestLoaderService.hide();
    };
    xhr.send();
  }

  private createTagElement(name: string): HTMLAnchorElement {
    // eslint-disable-next-line no-restricted-globals
    const tag = document.createElement('a');
    tag.download = name;

    return tag;
  }
}

// TODO:Replace usage of this function with downloadService.generateLinkAndDownload
export function generateLinkAndDownload(blob: Blob, fileName: string): void {
  // eslint-disable-next-line no-restricted-globals
  generateLink(window.URL.createObjectURL(blob), (anchor: HTMLAnchorElement) => {
    anchor.target = '_blank';
    anchor.download = fileName;
    anchor.dataset.downloadurl = ['text/plain', anchor.download, anchor.href].join(':');
  });
}

export function generateLinkAndClick(url: string): void {
  generateLink(url, (anchor: HTMLAnchorElement) => {
    anchor.target = '_blank';
  });
}

export function generateIframeAndPrint(blob: Blob): void {
  // eslint-disable-next-line no-restricted-globals
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  // eslint-disable-next-line no-restricted-globals
  iframe.src = window.URL.createObjectURL(blob);
  // eslint-disable-next-line no-restricted-globals
  document.body.appendChild(iframe);
  const iframeContentWindow = iframe.contentWindow;
  iframe.onload = () => {
    iframeContentWindow.focus();
    iframeContentWindow.print();
  };
  // eslint-disable-next-line no-restricted-globals
  iframeContentWindow.onafterprint = () => document.body.removeChild(iframe);
}

function generateLink(url: string, mutateFn: (anchor: HTMLAnchorElement) => void): void {
  // eslint-disable-next-line no-restricted-globals
  const anchor = document.createElement('a');
  anchor.href = url;
  mutateFn(anchor);
  // eslint-disable-next-line no-restricted-globals
  document.body.appendChild(anchor); // important in Firefox
  anchor.click();
  anchor.remove();
}

export async function convertBase64ToBlob(base64: string, type: string = 'application/pdf') {
  const res = await fetch(`data:${type};base64,${base64}`);

  return res.blob();
}

export function isResponseValidAvailableForDownload(response: HttpResponse<Blob>): boolean {
  return (
    !!response.headers.get(ContentHeaders.ContentType) && !!response.headers.get(ContentHeaders.ContentDisposition)
  );
}
